import { ReactElement, useState, useEffect } from 'react';
import { Leader, actions, ThunkDispatchType, RootState } from '../../../store';
import { FixedNoteField } from '../EditNote/EditNote';
import { ERROR_TYPE, EVENT_TYPE } from '../../../constants';
import { addFieldError, removeFieldError, FieldErrors, validateEmail } from '../../../utils/inputUtils';
import { trackEvent } from '../../../utils/appAnalyticsUtils';
import { useConfirmationModalWithUI } from '../../Common/ConfirmationModal';
import { CabModal, CabImageUpload, CabIcon } from '@CabComponents';
import { CabButton } from '@CabComponents/CabButton';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import colors from '../../../colors';
import { IoCloseCircle } from 'react-icons/io5';

interface ComponentProps {
  leader: Leader;
  isOpen: boolean;
  onDidDismiss: () => void;
}


type FieldKey = 'first_name'|'last_name'|'company'|'role'|'email'|'phone_number';

type Props = ComponentProps;

export const EditLeaderBasic = ({ leader, isOpen, onDidDismiss}: Props): ReactElement => {
  const user = useSelector((state: RootState) => state.auth.user);
  const managerOnlyProfileDeletion = useSelector(
    (state: RootState) => state.organization.manager_only_profile_deletion
  );

  const { confirm: confirmDelete, ConfirmationModalComponent } = useConfirmationModalWithUI();

  const dispatch = useDispatch<ThunkDispatchType>();
  const updateLeader = (
    leaderUpdate: Partial<Leader> & { id: number }, 
    leaderPic: File | null, 
    removedPic?: boolean,
  ) => dispatch(actions.leaders.updateLeader(leaderUpdate, leaderPic, removedPic));
  const deleteLeader = (leaderId: number) => dispatch(actions.leaders.deleteLeader(leaderId));

  const [leaderPic, setLeaderPic] = useState<File | null>(null);

  const [editedLeader, setEditedLeader] = useState<Leader>(leader);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect((): void => {
    if (isOpen) {
      setEditedLeader(leader);
      setFieldErrors({ });
    }
  }, [leader, isOpen]);

  const handleEmailChange = (email: string): void => {
    if (validateEmail(email)) {
      handleRemoveError('email', ERROR_TYPE.EMAIL);
    } else {
      handleAddError('email', ERROR_TYPE.EMAIL);
    }
  };

  const validateRequiredFieldChange = (fieldKey: FieldKey): ((newValue: string) => void) => {
    return (newValue: string): void => {
      if (newValue === '') {
        handleAddError(fieldKey, ERROR_TYPE.REQUIRED);
      } else {
        handleRemoveError(fieldKey, ERROR_TYPE.REQUIRED);
      }
    };
  };

  const changeFunc = (key: keyof Leader) => (value: string): void => {
    setEditedLeader((prev) => ({...prev, [key]: value}));
  };

  const handleAddError = (fieldKey: FieldKey, errorType: keyof typeof ERROR_TYPE): void => {
    setFieldErrors(addFieldError(fieldErrors, fieldKey, errorType));
  };

  const handleRemoveError = (fieldKey: FieldKey, errorType: keyof typeof ERROR_TYPE): void => {
    setFieldErrors(removeFieldError(fieldErrors, fieldKey, errorType));
  };

  const getErrors = () => {
    const fieldMessages: string[] = [];
    Object.values(fieldErrors).forEach(field => {
      Object.values(field).forEach((error): void => {
        if (!error.valid) {
          fieldMessages.push(error.message);
        }
      });
    });

    return fieldMessages.join('');
  };

  const handleCancel = (): void => {
    onDidDismiss();
  };

  const handleSave = (): void => {
    const errors = getErrors();
    if (!errors) {
      // If there was a photo originally but now no photo is selected, mark as removed
      const removedPic = !!(leader.pic_url && !leaderPic);
      updateLeader(editedLeader, leaderPic, removedPic).then((): void => {
        setLeaderPic(null);
        onDidDismiss();
      });
      trackEvent(EVENT_TYPE.UPDATE_EXEC);
    } else {
      setFormError(errors);
    }
  };

  const handleDelete = (): void => {
    deleteLeader(leader.id);
    trackEvent(EVENT_TYPE.DELETE_EXEC);
  };

  const handleDeleteWithConfirmation = async (): Promise<void> => {
    const confirmed = await confirmDelete({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this teammate?",
      confirmText: "Delete",
      cancelText: "Cancel",
      isDestructive: true,
      doubleConfirm: {
        title: "Are you sure you want to delete this teammate?",
        message: "All of the information you've stored will be deleted and unrecoverable.",
        confirmText: "Yes, Delete Teammate",
        cancelText: "Cancel"
      },
    });
    
    if (confirmed) {
      handleDelete();
      onDidDismiss();
    }
  };

  const noDeletePermission = !leader.permissions.standard.basic.delete;
  const isUserLeader = user?.profile.user_leader === leader.id;
  const managerOnlyDelete = managerOnlyProfileDeletion && !user?.permissions.WRITE_LEADER_DATA_ACCESS;

  return (
    <>
      <CabModal
        open={isOpen}
        onClose={handleCancel}
        closeIcon
        closeOnBackdropClick
        title='Basic Info'
        actionButtons={
          <Box sx={{ width: '100%', display: 'flex', 
            justifyContent: !user?.features.DISABLE_USER_LEADER_CONTROL ? 'space-between' : 'flex-end' }}>
            {!user?.features.DISABLE_USER_LEADER_CONTROL && (
              <CabButton 
                buttonType="tertiary" 
                color="error" 
                onClick={handleDeleteWithConfirmation}
                disabled={noDeletePermission || isUserLeader || managerOnlyDelete}
              >
                Delete Profile
              </CabButton>
            )}
            <CabButton color='primary' onClick={handleSave}>
              Save
            </CabButton>
          </Box>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column'
          }}>
          <CabImageUpload
            currentImage={leader.pic_url}
            avatarName={`${leader.first_name} ${leader.last_name}`}
            displayMode="avatar"
            avatarSize="large"
            avatarColor={leader.color}
            onImageSelect={(file) => {
              setLeaderPic(file);
            }}
            useDropzone={true}
            dragDropText="Drag & drop image or"
            clickText="click"
            fileTypeText="JPG or PNG. Max"
            maxSizeText="2.5Mb"
            sx={{ marginTop: 2, marginBottom: 2 }}
            dropzoneSx={{
              border: `1px solid ${colors.black200}`,
              borderRadius: 4,
              padding: 2
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center"
            }}>
            <CabButton 
              buttonType='text' 
              color='primary' 
              size='small'
              onClick={() => setLeaderPic(null)}
              disabled={!leader.pic_url && !leaderPic}
              icon={<CabIcon alt='Delete' Icon={IoCloseCircle} />}
            >
              Remove Photo
            </CabButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
            <FixedNoteField fieldLabelKey="first_name" placeholder="Enter a name" 
              value={editedLeader.first_name} changeFunc={changeFunc('first_name')}
              validateField={validateRequiredFieldChange('first_name')}/>

            <FixedNoteField fieldLabelKey="last_name" placeholder="Enter a name" 
              value={editedLeader.last_name} changeFunc={changeFunc('last_name')} 
              validateField={validateRequiredFieldChange('last_name')}/>

            <FixedNoteField fieldLabelKey="company" placeholder="Company" 
              value={editedLeader.company} changeFunc={changeFunc('company')} />

            <FixedNoteField fieldLabelKey="role" placeholder="Job title" 
              value={editedLeader.role} changeFunc={changeFunc('role')} />

            <FixedNoteField fieldLabelKey="email" placeholder="Email" 
              value={editedLeader.email} changeFunc={changeFunc('email')} 
              validateField={handleEmailChange}/>

            <FixedNoteField fieldLabelKey="phone_number" placeholder="Phone number" 
              value={editedLeader.phone_number} changeFunc={changeFunc('phone_number')}/>
          </Box>
        </Box>
      </CabModal>
      <CabModal
        open={!!formError}
        isAlert
        closeOnBackdropClick
        noFullScreen={true}
        onClose={(): void => setFormError(null)}
        title={'Errors'}
        text={formError || ''}
        actionButtons={
          <CabButton buttonType='primary' color='primary' onClick={(): void => setFormError(null)}>
            Okay
          </CabButton>
        }
      />
      <ConfirmationModalComponent />
    </>
  );
};

export default EditLeaderBasic;